import React, { useEffect, useState } from "react";
import { ContactAccessCoinInterface } from "../../interfaces/contact-access-coin.interface";
import { post, put } from "../../redux/ApiHelper";
import { successToast, errorToast } from "../../utils/toast.util";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";

interface CACPackageModalProps {
  isOpen: boolean;
  closeModal: () => void;
  isEdit?: boolean;
  editData?: ContactAccessCoinInterface | null;
  onSuccess?: () => void;
}

const CACPackageModal: React.FC<CACPackageModalProps> = ({
  isOpen,
  closeModal,
  isEdit = false,
  editData = null,
  onSuccess,
}) => {
  const { signedAdmin } = useSelector((state: RootState) => state.admin);
  const [formData, setFormData] = useState({
    coins: "",
    inrValue: "",
  });
  const [errors, setErrors] = useState({
    coins: "",
    inrValue: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isEdit && editData) {
      setFormData({
        coins: editData.coins?.toString() || "",
        inrValue: editData.inrValue?.toString() || "",
      });
    } else {
      setFormData({
        coins: "",
        inrValue: "",
      });
    }
    setErrors({ coins: "", inrValue: "" });
  }, [isEdit, editData, isOpen]);

  const validateForm = (): boolean => {
    const newErrors = { coins: "", inrValue: "" };
    let isValid = true;

    if (!formData.coins.trim()) {
      newErrors.coins = "Coins is required";
      isValid = false;
    } else if (parseFloat(formData.coins) <= 0) {
      newErrors.coins = "Coins must be greater than 0";
      isValid = false;
    }

    if (!formData.inrValue.trim()) {
      newErrors.inrValue = "INR Value is required";
      isValid = false;
    } else if (parseFloat(formData.inrValue) <= 0) {
      newErrors.inrValue = "INR Value must be greater than 0";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name as keyof typeof errors]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      const queryParams = new URLSearchParams({
        role: signedAdmin.role || "",
      });

      const packageData = {
        coins: parseFloat(formData.coins),
        inrValue: parseFloat(formData.inrValue),
      };

      if (isEdit && editData?._id) {
        await put(
          `/admin/cac/update/${editData._id}?${queryParams.toString()}`,
          packageData
        );
        successToast("Package updated successfully");
      } else {
        await post(
          `/admin/cac/create?${queryParams.toString()}`,
          packageData
        );
        successToast("Package created successfully");
      }

      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      const errorMessage =
        error?.message || "Failed to save package. Please try again.";
      errorToast(errorMessage);
      console.error("Error saving package:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-gray-800 w-full max-w-lg mx-4 p-6 rounded-lg shadow-lg max-h-screen overflow-auto border border-gray-700">
        <h2 className="text-xl font-semibold text-yellow-500 mb-4">
          {isEdit ? "Update" : "Create New"} CAC (Contact Access Coin) Package
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-gray-300 mb-2 text-left">
              tokens <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="coins"
              value={formData.coins}
              onChange={handleChange}
              step="any"
              min="0"
              className={`w-full px-4 py-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 ${
                errors.coins
                  ? "focus:ring-red-500 border-red-500"
                  : "focus:ring-yellow-500"
              }`}
              placeholder="Enter number of tokens"
            />
            {errors.coins && (
              <p className="text-red-500 text-sm mt-1 text-left">
                {errors.coins}
              </p>
            )}
          </div>

          <div>
            <label className="block text-gray-300 mb-2 text-left">
              INR Value <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="inrValue"
              value={formData.inrValue}
              onChange={handleChange}
              step="any"
              min="0"
              className={`w-full px-4 py-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 ${
                errors.inrValue
                  ? "focus:ring-red-500 border-red-500"
                  : "focus:ring-yellow-500"
              }`}
              placeholder="Enter INR value"
            />
            {errors.inrValue && (
              <p className="text-red-500 text-sm mt-1 text-left">
                {errors.inrValue}
              </p>
            )}
          </div>

          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              onClick={closeModal}
              className="px-4 py-2 bg-gray-600 text-gray-200 rounded-md hover:bg-gray-500 transition"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`px-4 py-2 rounded-md transition ${
                isSubmitting
                  ? "bg-yellow-600 opacity-50 cursor-not-allowed"
                  : "bg-yellow-500 text-gray-900 hover:bg-yellow-400"
              }`}
              disabled={isSubmitting}
            >
              {isSubmitting
                ? isEdit
                  ? "Updating..."
                  : "Creating..."
                : isEdit
                ? "Update Package"
                : "Create Package"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CACPackageModal;
